import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import ContentIdeas from "@/components/ContentIdeas";
import ReferralCard from "@/components/ReferralCard";
import GoalCard from "@/components/GoalCard";
import VideoCreditsMeter from "@/components/VideoCreditsMeter";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, Eye, Heart, TrendingUp, Zap, MessageCircle, Share2, Rocket, Loader2, AtSign, Target,
  Bell, Send, Plus, Sparkles, Clock,
} from "lucide-react";

const ALL_PLATFORMS = [
  { id: "tiktok", name: "TikTok" }, { id: "instagram", name: "Instagram" },
  { id: "youtube", name: "YouTube" }, { id: "facebook", name: "Facebook" },
];

const fmt = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n ?? 0);
};

const fmtHour = (h) => {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};

const StatCard = ({ icon: Icon, label, value, sub, accent = "primary", i = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
    className="rounded-xl border border-border/50 bg-card p-5 hover:-translate-y-1 transition-transform duration-300" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent === "primary" ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="font-heading text-3xl font-black">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </motion.div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <div className="text-muted-foreground mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="font-semibold" style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
};

const PLATFORM_COLORS = { tiktok: "#FE2C55", instagram: "#E1306C", youtube: "#FF0000", facebook: "#1877F2" };

const CombinedView = ({ overview, onSelect }) => {
  if (!overview) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  const t = overview.totals || {};
  const chartData = (overview.series || []).map((s) => ({ date: s.date.slice(5), followers: s.followers }));
  return (
    <div className="space-y-6" data-testid="all-platforms-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard i={0} icon={Users} label="Total followers" value={fmt(t.real_followers)} sub="across all platforms" />
        <StatCard i={1} icon={TrendingUp} label="Projected total" value={fmt(t.current_followers)} sub={`+${fmt(t.followers_gained)} forecast`} accent="secondary" />
        <StatCard i={2} icon={Eye} label="Total views" value={fmt(t.views)} sub="all platforms" />
        <StatCard i={3} icon={Heart} label="Avg engagement" value={`${t.engagement_rate}%`} sub="blended rate" accent="secondary" />
      </div>
      <div className="rounded-xl border border-border/50 bg-card p-6" data-testid="all-platforms-chart">
        <h3 className="font-heading text-lg font-semibold mb-6">Combined projected growth <span className="text-xs font-normal text-muted-foreground">(all platforms)</span></h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00F2FE" stopOpacity={0.4} /><stop offset="100%" stopColor="#00F2FE" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" vertical={false} />
              <XAxis dataKey="date" stroke="hsl(240 5% 65%)" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} />
              <YAxis stroke="hsl(240 5% 65%)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmt} width={40} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="followers" name="Followers" stroke="#00F2FE" strokeWidth={2.5} fill="url(#cg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(overview.platforms || []).map((p) => (
          <button key={p.platform} onClick={() => onSelect?.(p.platform)} data-testid={`overview-platform-${p.platform}`}
            className="text-left rounded-xl border border-border/50 bg-card p-5 hover:border-primary/40 hover:-translate-y-0.5 transition-all cursor-pointer">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: PLATFORM_COLORS[p.platform] || "#00F2FE" }} />
                <span className="font-heading font-bold">{p.name}</span>
                <span className="text-xs text-muted-foreground">@{p.handle}</span>
              </div>
              <span className="text-xs text-primary font-semibold flex items-center gap-1">View <TrendingUp className="w-3 h-3" /></span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="font-heading text-xl font-black">{fmt(p.real_followers)}</div><div className="text-[11px] text-muted-foreground">followers</div></div>
              <div><div className="font-heading text-xl font-black text-primary">+{fmt(p.followers_gained)}</div><div className="text-[11px] text-muted-foreground">gained</div></div>
              <div><div className="font-heading text-xl font-black">{fmt(p.views)}</div><div className="text-[11px] text-muted-foreground">views</div></div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState(null);
  const [overview, setOverview] = useState(null);
  const [recapOn, setRecapOn] = useState(true);
  const [digestOn, setDigestOn] = useState(true);
  const [reminderHour, setReminderHour] = useState(8);
  const [dailyReminderOn, setDailyReminderOn] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ platform: "", handle: "", followers: "" });
  const [showLog, setShowLog] = useState(false);
  const [logCount, setLogCount] = useState("");

  const load = (pf) => api.get(`/growth/dashboard${pf ? `?platform=${pf}` : ""}`).then((r) => {
    setData(r.data);
    setPlatform(r.data.selected_platform);
    setRecapOn(r.data.user?.email_weekly_recap !== false);
    setDigestOn(r.data.user?.email_best_days_digest !== false);
    setReminderHour(r.data.user?.reminder_hour ?? 8);
    setDailyReminderOn(r.data.user?.email_daily_reminder !== false);
  }).catch(() => {});

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  // Sync the creator's browser timezone once so reminders & schedules follow local time.
  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && user && user.timezone !== tz) {
      api.post("/email/preferences", { timezone: tz }).then(() => refreshUser()).catch(() => {});
    }
  }, [user?.timezone]);

  const switchPlatform = (pf) => {
    setPlatform(pf);
    if (pf === "all") api.get("/growth/overview").then((r) => setOverview(r.data)).catch(() => {});
    else load(pf);
  };

  const toggleRecap = async (val) => {
    setRecapOn(val);
    try { await api.post("/email/preferences", { enabled: val }); await refreshUser(); toast.success(val ? "Weekly recaps on" : "Weekly recaps off"); }
    catch { toast.error("Could not update preference"); }
  };

  const sendRecap = async () => {
    setSending(true);
    try { await api.post("/email/recap"); toast.success("Recap email sent!"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send recap"); }
    finally { setSending(false); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try { await api.post("/digest/best-days/send"); toast.success("Best Days Digest sent to your inbox! 🚀"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send digest"); }
    finally { setSendingDigest(false); }
  };

  const toggleDigest = async (val) => {
    setDigestOn(val);
    try { await api.post("/email/preferences", { best_days_digest: val }); await refreshUser(); toast.success(val ? "Best Days Digest on" : "Best Days Digest off"); }
    catch { toast.error("Could not update preference"); }
  };

  const changeReminderHour = async (val) => {
    const h = parseInt(val, 10);
    setReminderHour(h);
    try { await api.post("/email/preferences", { reminder_hour: h }); await refreshUser(); toast.success(`Daily reminders set to ${fmtHour(h)}`); }
    catch { toast.error("Could not update reminder time"); }
  };

  const toggleDailyReminder = async (val) => {
    setDailyReminderOn(val);
    try { await api.post("/email/preferences", { daily_reminder: val }); await refreshUser(); toast.success(val ? "Daily reminders on" : "Daily reminders off"); }
    catch { toast.error("Could not update preference"); }
  };

  const addPlatform = async () => {
    if (!addForm.platform || !addForm.handle.trim()) { toast.error("Pick a platform and add a handle"); return; }
    try {
      await api.post("/platforms", { platform: addForm.platform, handle: addForm.handle, starting_followers: parseInt(addForm.followers || "0", 10) });
      toast.success("Platform added");
      const pf = addForm.platform;
      setShowAdd(false); setAddForm({ platform: "", handle: "", followers: "" });
      await load(pf);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add platform"); }
  };

  const logFollowers = async () => {
    const c = parseInt(logCount || "", 10);
    if (isNaN(c) || c < 0) { toast.error("Enter your current follower count"); return; }
    try {
      await api.post("/followers/log", { platform, count: c });
      toast.success("Follower count logged!");
      setShowLog(false); setLogCount("");
      await load(platform);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log"); }
  };

  if (loading) {
    return <DashboardShell><div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></DashboardShell>;
  }

  const growth = data?.growth;
  const plan = data?.plan;
  const active = growth?.active;
  const platforms = data?.platforms || [];
  const curHandle = platforms.find((p) => p.platform === platform)?.handle;
  const curName = platforms.find((p) => p.platform === platform)?.name;
  const available = ALL_PLATFORMS.filter((p) => !platforms.some((x) => x.platform === p.id));
  const series = growth?.series || [];
  const chartData = series.map((s) => ({ date: s.date.slice(5), followers: s.followers, gained: s.gained, views: s.views }));
  const t = growth?.totals || {};

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">Growth Overview</h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
              {curHandle && <span className="flex items-center gap-1"><AtSign className="w-3.5 h-3.5" />{curHandle}</span>}
              {curName && <span className="text-primary font-medium">{curName}</span>}
              {user?.target_audience?.niche && <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{user.target_audience.niche}</span>}
            </div>
          </div>
          {active ? (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-semibold text-primary" data-testid="campaign-status">Campaign live · {plan?.name}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5">
              <span className="w-2 h-2 rounded-full bg-muted-foreground" />
              <span className="text-sm font-semibold text-muted-foreground" data-testid="campaign-status">No active campaign</span>
            </div>
          )}
        </div>

        {platforms.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {platforms.length > 1 && (
              <button onClick={() => switchPlatform("all")} data-testid="platform-tab-all"
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${platform === "all" ? "bg-primary text-background border-primary" : "border-white/15 text-muted-foreground hover:border-white/40"}`}>
                All Platforms
              </button>
            )}
            {platforms.map((p) => (
              <button key={p.platform} onClick={() => switchPlatform(p.platform)} data-testid={`platform-tab-${p.platform}`}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${platform === p.platform ? "bg-primary text-background border-primary" : "border-white/15 text-muted-foreground hover:border-white/40"}`}>
                {p.name}
              </button>
            ))}
            {available.length > 0 && (
              <button onClick={() => setShowAdd(true)} data-testid="add-platform-btn"
                className="px-4 py-2 rounded-full text-sm font-medium border border-dashed border-white/20 text-muted-foreground hover:border-white/40 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add platform
              </button>
            )}
            {active && platform !== "all" && (
              <button onClick={() => setShowLog(true)} data-testid="log-followers-btn"
                className="ml-auto px-4 py-2 rounded-full text-sm font-medium border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Log followers
              </button>
            )}
          </div>
        )}

        <Dialog open={showLog} onOpenChange={setShowLog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading">Log your real followers</DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <Label className="text-sm">Current {curName} follower count</Label>
              <Input type="number" min="0" value={logCount} onChange={(e) => setLogCount(e.target.value)} placeholder="Check your profile and enter it"
                className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="log-count-input" />
              <p className="text-xs text-muted-foreground mt-2">This real number powers your goals, badges and leaderboard rank.</p>
            </div>
            <DialogFooter>
              <Button onClick={logFollowers} className="rounded-full bg-primary text-background font-bold" data-testid="log-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {active && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 rounded-xl border border-border/50 bg-card p-4" data-testid="recap-settings">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Bell className="w-4 h-4" /></div>
              <div>
                <div className="text-sm font-semibold">Weekly recap emails</div>
                <div className="text-xs text-muted-foreground">Get your growth summary in your inbox every Monday</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Switch checked={recapOn} onCheckedChange={toggleRecap} data-testid="recap-toggle" />
              <Button variant="outline" size="sm" onClick={sendRecap} disabled={sending} className="rounded-full border-white/15" data-testid="send-recap-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send now</>}
              </Button>
            </div>
          </div>
        )}

        {active && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.07] to-transparent p-4" data-testid="digest-settings">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
              <div>
                <div className="text-sm font-semibold">Best Days Digest</div>
                <div className="text-xs text-muted-foreground">Your top posting times + a trending idea, emailed every Sunday</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Switch checked={digestOn} onCheckedChange={toggleDigest} data-testid="digest-toggle" />
              <Button variant="outline" size="sm" onClick={sendDigest} disabled={sendingDigest} className="rounded-full border-primary/30 text-primary" data-testid="send-digest-btn">
                {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send my digest</>}
              </Button>
            </div>
          </div>
        )}

        {active && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 rounded-xl border border-border/50 bg-card p-4" data-testid="reminder-settings">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Clock className="w-4 h-4" /></div>
              <div>
                <div className="text-sm font-semibold">Daily post reminder</div>
                <div className="text-xs text-muted-foreground">Email me the day's scheduled posts{dailyReminderOn ? ` at this time (${data?.user?.timezone || "UTC"})` : " — currently off"}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Switch checked={dailyReminderOn} onCheckedChange={toggleDailyReminder} data-testid="daily-reminder-toggle" />
              <Select value={String(reminderHour)} onValueChange={changeReminderHour} disabled={!dailyReminderOn}>
                <SelectTrigger className="h-10 w-36 bg-transparent border-white/15 rounded-full disabled:opacity-40" data-testid="reminder-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">Add a platform</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm">Platform</Label>
                <Select value={addForm.platform} onValueChange={(v) => setAddForm({ ...addForm, platform: v })}>
                  <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="add-platform-select"><SelectValue placeholder="Choose platform" /></SelectTrigger>
                  <SelectContent>{available.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Handle</Label>
                <Input value={addForm.handle} onChange={(e) => setAddForm({ ...addForm, handle: e.target.value })} placeholder="yourhandle"
                  className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="add-platform-handle" />
              </div>
              <div>
                <Label className="text-sm">Current followers</Label>
                <Input type="number" min="0" value={addForm.followers} onChange={(e) => setAddForm({ ...addForm, followers: e.target.value })} placeholder="e.g. 800"
                  className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="add-platform-followers" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={addPlatform} className="rounded-full bg-primary text-background font-bold" data-testid="add-platform-submit">Add platform</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {platform === "all" ? (
          <CombinedView overview={overview} onSelect={switchPlatform} />
        ) : !active ? (
          <div className="space-y-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent p-10 md:p-14 text-center" data-testid="no-campaign-cta">
            <div className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-3xl font-black tracking-tight">Launch your growth campaign</h2>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">Pick a plan and our AI starts promoting your content to targeted viewers. Your live analytics will appear here.</p>
            <Button onClick={() => navigate("/plans")} className="mt-7 rounded-full h-12 px-8 bg-primary text-background font-bold hover:bg-primary/90 transition-colors" data-testid="choose-plan-btn">
              Choose a plan <Zap className="w-4 h-4 ml-1" fill="currentColor" />
            </Button>
          </motion.div>
          <div className="max-w-md"><ReferralCard /></div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <GoalCard goal={data?.goal} platform={platform} platformName={curName} onUpdated={() => load(platform)} />
            </div>
            <div className="mb-6"><VideoCreditsMeter /></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard i={0} icon={Users} label="Followers" value={fmt(data?.real_followers ?? growth.current_followers)} sub="your logged real count" />
              <StatCard i={1} icon={TrendingUp} label="Daily avg" value={`+${growth.avg_daily_followers}`} sub="followers / day" accent="secondary" />
              <StatCard i={2} icon={Eye} label="Total views" value={fmt(t.views)} sub={`${growth.days_active} days active`} />
              <StatCard i={3} icon={Heart} label="Engagement" value={`${growth.engagement_rate}%`} sub="avg. rate" accent="secondary" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2 rounded-xl border border-border/50 bg-card p-6" data-testid="follower-chart">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-lg font-semibold">Projected growth <span className="text-xs font-normal text-muted-foreground">(forecast)</span></h3>
                  <span className="text-xs font-bold text-primary flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{fmt(t.followers_gained)}</span>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00F2FE" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#00F2FE" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" vertical={false} />
                      <XAxis dataKey="date" stroke="hsl(240 5% 65%)" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} />
                      <YAxis stroke="hsl(240 5% 65%)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmt} width={40} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="followers" name="Followers" stroke="#00F2FE" strokeWidth={2.5} fill="url(#fg)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-xl border border-border/50 bg-card p-6" data-testid="engagement-breakdown">
                <h3 className="font-heading text-lg font-semibold mb-6">Engagement</h3>
                <div className="space-y-5">
                  {[
                    { icon: Heart, label: "Likes", value: t.likes, color: "text-secondary" },
                    { icon: MessageCircle, label: "Comments", value: t.comments, color: "text-primary" },
                    { icon: Share2, label: "Shares", value: t.shares, color: "text-chart-3" },
                    { icon: Eye, label: "Views", value: t.views, color: "text-chart-4" },
                  ].map((m) => (
                    <div key={m.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <m.icon className={`w-4 h-4 ${m.color}`} />
                        <span className="text-sm text-muted-foreground">{m.label}</span>
                      </div>
                      <span className="font-heading font-bold">{fmt(m.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border/50 bg-card p-6" data-testid="views-chart">
              <h3 className="font-heading text-lg font-semibold mb-6">Daily views & new followers</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" vertical={false} />
                    <XAxis dataKey="date" stroke="hsl(240 5% 65%)" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} />
                    <YAxis stroke="hsl(240 5% 65%)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmt} width={40} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                    <Bar dataKey="views" name="Views" fill="#FE2C55" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="gained" name="New followers" fill="#00F2FE" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <div className="lg:col-span-2"><ContentIdeas platform={platform} /></div>
              <ReferralCard />
            </div>
          </>
        )}
      </div>
    </DashboardShell>
  );
};

export default Dashboard;
